import React, { useState } from 'react';
import { ShoppingBag, Ruler, MessageCircle, Sparkles, X, ZoomIn } from 'lucide-react';
import { products } from '../utils/mock';
import { Button } from '../components/ui/button';

const Products = () => {
  const [enlargedProduct, setEnlargedProduct] = useState(null);
  const handleBuyOnWhatsApp = (product) => {
    const message = `*New Product Order*\n\n` +
      `Product: ${product.name}\n` +
      `Price: ${product.priceLabel || `₹${product.price}`}\n\n` +
      `Hi FutureBuildX, I would like to buy this product. Please share the details.`;
    const whatsappURL = `https://wa.me/919762717034?text=${encodeURIComponent(message)}`;
    window.open(whatsappURL, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6" data-testid="products-page-title">
            3D <span className="text-cyan-400">Products</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Explore our collection of premium 3D printed products, crafted with precision and creativity.
          </p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="products-grid">
            {products.map((product) => (
              <div
                key={product.id}
                data-testid={`product-card-${product.id}`}
                className="group bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950/50 backdrop-blur-sm border border-cyan-500/20 rounded-2xl overflow-hidden hover:border-cyan-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/20"
              >
                {/* Product Image */}
                <div
                  className="relative overflow-hidden bg-slate-800/50 cursor-zoom-in"
                  onClick={() => setEnlargedProduct(product)}
                  data-testid={`product-image-wrapper-${product.id}`}
                >
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-80 object-cover object-center transform group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-950/0 group-hover:bg-slate-950/30 transition-all duration-300">
                    <ZoomIn size={36} className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 drop-shadow-lg" />
                  </div>
                  <div className="absolute top-4 right-4 bg-cyan-500 text-white px-4 py-1.5 rounded-full font-bold text-lg shadow-lg shadow-cyan-500/40" data-testid={`product-price-${product.id}`}>
                    {product.priceLabel || `₹${product.price}`}
                  </div>
                </div>

                {/* Product Details */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-2" data-testid={`product-name-${product.id}`}>
                    {product.name}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">{product.description}</p>

                  {product.note && (
                    <div className="flex items-center space-x-2 text-cyan-400 text-sm mb-4">
                      <Ruler size={16} className="flex-shrink-0" />
                      <span>{product.note}</span>
                    </div>
                  )}

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {product.tags.map((tag) => (
                      <span
                        key={tag}
                        className="flex items-center space-x-1 px-3 py-1 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-xs"
                      >
                        <Sparkles size={12} />
                        <span>{tag}</span>
                      </span>
                    ))}
                  </div>

                  {/* Buy Button */}
                  <Button
                    onClick={() => handleBuyOnWhatsApp(product)}
                    data-testid={`buy-whatsapp-btn-${product.id}`}
                    className="w-full bg-cyan-500 hover:bg-cyan-600 text-white font-semibold py-6"
                  >
                    <MessageCircle size={18} className="mr-2" />
                    Buy on WhatsApp
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Custom Order CTA */}
          <div className="mt-16 text-center bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950/50 border border-cyan-500/20 rounded-2xl p-10">
            <ShoppingBag className="mx-auto text-cyan-400 mb-4" size={40} />
            <h2 className="text-2xl font-bold text-white mb-3">Want a Custom 3D Printed Product?</h2>
            <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
              We can bring your ideas to life! Contact us for custom 3D printing orders, personalized designs, and bulk requirements.
            </p>
            <a
              href={`https://wa.me/919762717034?text=${encodeURIComponent("Hi FutureBuildX, I'm interested in a custom 3D printed product. Please share the details.")}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="custom-order-whatsapp-btn"
              className="inline-flex items-center justify-center bg-cyan-500 hover:bg-cyan-600 text-white font-semibold px-8 py-4 rounded-md transition-colors duration-300"
            >
              <MessageCircle size={18} className="mr-2" />
              Request Custom Order
            </a>
          </div>
        </div>
      </section>

      {/* Image Lightbox */}
      {enlargedProduct && (
        <div
          className="fixed inset-0 z-[100] bg-slate-950/95 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setEnlargedProduct(null)}
          data-testid="product-lightbox"
        >
          <button
            onClick={() => setEnlargedProduct(null)}
            data-testid="lightbox-close-btn"
            className="absolute top-6 right-6 text-white bg-slate-800/80 hover:bg-cyan-500 rounded-full p-3 transition-colors duration-300 z-10"
          >
            <X size={24} />
          </button>
          <div className="max-w-4xl w-full max-h-[90vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={enlargedProduct.image}
              alt={enlargedProduct.name}
              className="max-h-[75vh] w-auto object-contain rounded-xl shadow-2xl shadow-cyan-500/20"
              data-testid="lightbox-image"
            />
            <div className="mt-4 text-center">
              <h3 className="text-white text-xl font-bold">{enlargedProduct.name}</h3>
              <p className="text-cyan-400 text-lg font-semibold mt-1">{enlargedProduct.priceLabel || `₹${enlargedProduct.price}`}</p>
              <Button
                onClick={() => handleBuyOnWhatsApp(enlargedProduct)}
                data-testid="lightbox-buy-btn"
                className="mt-4 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold px-8"
              >
                <MessageCircle size={18} className="mr-2" />
                Buy on WhatsApp
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
